/**
 * The RBAC screen's shapes, as the platform answers them.
 *
 * The matrix is grouped the way the screen stacks its sections -- Functions,
 * Templates, Masters -- and every item carries its five booleans. No row, column
 * or section state is carried: those are derived here from the booleans, because
 * computing them on both sides is how a badge and its checkboxes come to
 * disagree.
 */

/**
 * The five actions every item is scored against, in column order.
 *
 * READ leads because it is the one every role already holds: its column is drawn ticked and disabled
 * -- see `EDITABLE_KEYS`. ACCESS follows as the gate: it decides whether the item appears in the role's
 * sidebar, and holding any of CREATE, UPDATE or DELETE on a row switches it on -- see `IMPLIES_VIEW`.
 *
 * The keys are the platform's own -- `view` is what the API names the ACCESS column, and the save sends
 * that name. Only the heading was renamed; see `PERMISSION_LABELS`.
 */
export const PERMISSION_KEYS = ['read', 'view', 'create', 'update', 'delete'] as const;

export type PermissionKey = (typeof PERMISSION_KEYS)[number];

/** The reference's column headings, in the same order. */
export const PERMISSION_LABELS: Readonly<Record<PermissionKey, string>> = {
  read: 'READ',
  /* The `view` action, drawn as ACCESS: the heading administrators read, not the name the API is sent. */
  view: 'ACCESS',
  create: 'CREATE',
  update: 'UPDATE',
  delete: 'DELETE',
};

/**
 * The actions that imply ACCESS: a row holding any one of CREATE, UPDATE or DELETE has its
 * ACCESS ticked, on that row only. Clearing ACCESS clears all three. READ is deliberately absent.
 */
export const IMPLIES_VIEW: readonly PermissionKey[] = ['create', 'update', 'delete'];

/**
 * The columns an administrator can change. READ is not among them: every role holds READ on every item,
 * so its column is drawn ticked and disabled.
 */
export const EDITABLE_KEYS: readonly PermissionKey[] = PERMISSION_KEYS.filter((key) => key !== 'read');

/** A three-state checkbox: everything, nothing, or some of what it governs. */
export type CheckState = 'checked' | 'unchecked' | 'indeterminate';

/** One row of a matrix section. */
export interface MatrixItem {
  featureId: number;
  /** What the screen draws -- the menu's label where it has one (see `displayLabels`). */
  name: string;
  /**
   * The `os_feature.ofe_name` behind the row, where the drawn `name` differs from it -- "RFQ Template"
   * drawn as "BID Template". Guards and routes name features by this; the screen only shows `name`.
   */
  feature?: string;
  create: boolean;
  read: boolean;
  update: boolean;
  delete: boolean;
  /** Whether the item appears in the role's sidebar -- the ACCESS column. */
  view: boolean;
  /**
   * The item's actions are fixed on for this role: the Dashboard for every role, and RBAC for Admin and Mega
   * Admin, and every item for Mega Admin. Reported by the server per
   * role, which refuses a save turning one off; drawn ticked and disabled, and left on by every toggle.
   */
  locked?: boolean;
  /**
   * The item's actions are kept as stored for this role: Organization Setup, for every role but Mega Admin. Reported
   * by the server per role, which keeps the stored grants whatever a save sends; drawn as they are, disabled, and
   * left untouched by every toggle.
   */
  frozen?: boolean;
}

/** One section: Functions, Templates or Masters. */
export interface MatrixGroup {
  /** FUNCTION, TEMPLATE or MASTER. */
  key: string;
  /** The heading the section is drawn under. */
  title: string;
  itemCount: number;
  granted: number;
  total: number;
  items: MatrixItem[];
}

/** A role's whole matrix. */
export interface PermissionMatrix {
  roleId: number;
  roleName: string;
  roleDescription?: string | null;
  granted: number;
  total: number;
  groups: MatrixGroup[];
  /**
   * Items the platform scores that no section draws -- a MASTER feature with no Masters menu entry (see
   * `inMastersMenuOrder`). Never edited here, and sent back unchanged on save, because the save replaces
   * the role's whole matrix and an omitted item would read as a revocation.
   */
  retained?: MatrixItem[];
}

/** A role, as the rail draws it. */
export interface RbacRole {
  id: number;
  name: string;
  description?: string | null;
  isDefault?: boolean | null;
  /** Permissions held across the whole matrix -- the number shown beside the name. */
  grantedCount?: number | null;
}

/** What a save answers: the new count, and the items left holding nothing. */
export interface MatrixSaveResult {
  roleId: number;
  roleName: string;
  granted: number;
  total: number;
  ungranted: UngrantedGroup[];
}

/** One group's ungranted items, for the save-time warning. */
export interface UngrantedGroup {
  key: string;
  title: string;
  count: number;
  names: string[];
}

/** What the New Role dialog collects. */
export interface NewRolePayload {
  name: string;
  description: string;
}
